import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { ReturnsCsvImportResponse, ReturnsService } from '../../../services/returns.service';

/**
 * Popup di caricamento dell'Excel di chiusura dei resi, aperto dal pulsante
 * arancione della sezione Resi.
 *
 * Alla conferma il file viene letto nel browser, convertito nel CSV atteso dal
 * backend (colonne separate da punto e virgola, data in dd/MM/yyyy HH:mm) e
 * inviato a POST /pallets/returns/import-csv, che chiude i resi riconosciuti:
 * l'esito, comprese le righe saltate e quelle scartate, viene mostrato qui.
 */
@Component({
  selector: 'app-close-returns-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './close-returns-popup.component.html',
  styleUrl: './close-returns-popup.component.css'
})
export class CloseReturnsPopupComponent implements OnChanges {

  @Input() visible: boolean = false;

  @Output() closed = new EventEmitter<void>();
  /** Esito dell'importazione restituito dal backend per il file confermato */
  @Output() imported = new EventEmitter<ReturnsCsvImportResponse>();

  selectedFile: File | null = null;
  isDragOver = false;
  errorMessage = '';
  /** True mentre è mostrata la guida sul formato del file al posto della dropzone */
  showHelp = false;

  /** True durante lettura del file e invio al backend */
  isUploading = false;
  /** Esito dell'ultimo invio; finché è valorizzato prende il posto della dropzone */
  result: ReturnsCsvImportResponse | null = null;

  private readonly allowedExtensions = ['.xlsx', '.xls'];
  private readonly maxFileSizeMb = 10;

  /** Separatore accettato dall'endpoint (l'altro è la tabulazione) */
  private readonly csvSeparator = ';';
  /** Posizione della colonna data fra quelle attese */
  private readonly dateColumnIndex = 3;

  /** Colonne attese nel file, nell'ordine: usate da guida e file di esempio */
  readonly exampleHeader = ['Codice Reso', 'Codice Bolla', 'Codice Dealer', 'Data'];
  readonly exampleRows: string[][] = [
    ['R2026000123', 'B2026004567', 'DLR00123', '31/07/2026 14:30'],
    ['R2026000124', 'B2026004568', 'DLR00089', '31/07/2026 15:05']
  ];

  constructor(private returnsService: ReturnsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reset();
    }
  }

  private reset(): void {
    this.selectedFile = null;
    this.isDragOver = false;
    this.errorMessage = '';
    this.showHelp = false;
    this.isUploading = false;
    this.result = null;
  }

  /** Alterna dropzone e guida sul formato; il file già scelto resta in memoria */
  toggleHelp(): void {
    this.showHelp = !this.showHelp;
    this.isDragOver = false;
  }

  /** Excel di esempio con intestazione e due righe compilate */
  downloadExampleFile(): void {
    const worksheet = XLSX.utils.aoa_to_sheet([this.exampleHeader, ...this.exampleRows]);
    worksheet['!cols'] = this.exampleHeader.map(() => ({ wch: 20 }));
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'chiusura-resi-esempio.xlsx');
  }

  onClose(): void {
    this.closed.emit();
  }

  // ===== Scelta del file =====

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.pickFile(file);
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.pickFile(file);
    }
    // Permette di riselezionare lo stesso file dopo una rimozione
    input.value = '';
  }

  private pickFile(file: File): void {
    this.errorMessage = '';

    const name = file.name.toLowerCase();
    if (!this.allowedExtensions.some(ext => name.endsWith(ext))) {
      this.errorMessage = `"${file.name}": formato non supportato, sono accettati solo file Excel (${this.allowedExtensions.join(', ')})`;
      return;
    }
    if (file.size > this.maxFileSizeMb * 1024 * 1024) {
      this.errorMessage = `"${file.name}": supera il limite di ${this.maxFileSizeMb} MB`;
      return;
    }

    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.errorMessage = '';
  }

  /** Dimensione del file scelto in forma leggibile (KB / MB) */
  get selectedFileSizeLabel(): string {
    if (!this.selectedFile) {
      return '';
    }
    const bytes = this.selectedFile.size;
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  // ===== Invio =====

  /** Legge l'Excel, lo converte in CSV e lo manda al backend */
  onSubmit(): void {
    if (!this.selectedFile || this.isUploading) {
      return;
    }
    this.errorMessage = '';
    this.result = null;
    this.isUploading = true;

    this.fileToCsv(this.selectedFile)
      .then(csv => {
        if (!csv) {
          this.isUploading = false;
          this.errorMessage = `"${this.selectedFile?.name}": non contiene righe da inviare`;
          return;
        }
        this.returnsService.importReturnsCsv(csv).subscribe({
          next: (res) => {
            this.isUploading = false;
            this.result = res;
            this.imported.emit(res);
          },
          error: (err) => {
            this.isUploading = false;
            this.errorMessage = err?.message || 'Errore durante l\'invio del file';
          }
        });
      })
      .catch(err => {
        console.error('Errore nella lettura dell\'Excel di chiusura resi:', err);
        this.isUploading = false;
        this.errorMessage = `"${this.selectedFile?.name}": impossibile leggere il contenuto, verifica che sia un Excel valido`;
      });
  }

  /** Torna alla dropzone dopo un esito, per caricare un altro file */
  loadAnotherFile(): void {
    this.result = null;
    this.selectedFile = null;
    this.errorMessage = '';
  }

  /**
   * Contenuto CSV del primo foglio dell'Excel: solo le quattro colonne attese,
   * separate da punto e virgola. L'intestazione, se c'è, viene inviata così
   * com'è perché per l'endpoint è facoltativa.
   */
  private async fileToCsv(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });

    const sheetName = workbook.SheetNames[0];
    const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
    if (!sheet || !sheet['!ref']) {
      return '';
    }

    const range = XLSX.utils.decode_range(sheet['!ref']);
    const lines: string[] = [];

    for (let row = range.s.r; row <= range.e.r; row++) {
      const cells: string[] = [];
      for (let column = 0; column < this.exampleHeader.length; column++) {
        const cell: XLSX.CellObject | undefined =
          sheet[XLSX.utils.encode_cell({ r: row, c: range.s.c + column })];
        cells.push(this.toCsvCell(cell, column));
      }
      // Le righe completamente vuote sarebbero scartate dal backend come errori
      if (cells.some(value => value !== '')) {
        lines.push(cells.join(this.csvSeparator));
      }
    }

    return lines.join('\n');
  }

  /** Valore di una cella nel formato atteso dall'endpoint */
  private toCsvCell(cell: XLSX.CellObject | undefined, columnIndex: number): string {
    if (!cell || cell.v == null || cell.v === '') {
      return '';
    }

    // Excel tiene le date come numero seriale: il formato con cui è mostrata
    // nel file non è detto sia quello atteso, quindi si riformatta sempre.
    if (columnIndex === this.dateColumnIndex && cell.t === 'n' && typeof cell.v === 'number') {
      return this.formatExcelDate(cell.v);
    }

    // `w` è il testo come appare in Excel: conserva gli zeri iniziali dei
    // codici che `v` restituirebbe come numero
    return this.sanitizeCsvValue(cell.w != null ? cell.w : String(cell.v));
  }

  /**
   * Seriale Excel in dd/MM/yyyy HH:mm. I conti sono fatti in UTC e letti in
   * UTC: così l'orario inviato è quello scritto nel foglio, indipendentemente
   * dal fuso del browser.
   */
  private formatExcelDate(serial: number): string {
    const days = Math.floor(serial);
    const millisInDay = Math.round((serial - days) * 86400000);
    // Nel sistema data 1900 di Excel il seriale 1 è il 01/01/1900, quindi
    // l'origine dei conteggi è il 30/12/1899
    const date = new Date(Date.UTC(1899, 11, 30) + days * 86400000 + millisInDay);

    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`
      + ` ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  }

  /** Toglie i caratteri che spezzerebbero righe e colonne del CSV */
  private sanitizeCsvValue(value: string): string {
    return value.replace(/[;\t\r\n]+/g, ' ').trim();
  }

  // ===== Esito =====

  /** True se il backend ha scartato almeno una riga perché malformata */
  get hasErrorRows(): boolean {
    return (this.result?.errorRows ?? 0) > 0;
  }

  /** True se almeno una riga valida non è stata applicata */
  get hasSkippedRows(): boolean {
    return (this.result?.skippedRows ?? 0) > 0;
  }

  /** True se il file è stato importato senza righe saltate né scartate */
  get isFullyApplied(): boolean {
    return !!this.result && !this.hasErrorRows && !this.hasSkippedRows;
  }

  /** Titolo del pannello di esito */
  get resultTitle(): string {
    if (!this.result) {
      return '';
    }
    if (this.result.appliedRows === 0) {
      return 'Nessun reso chiuso';
    }
    return this.isFullyApplied
      ? 'Resi chiusi correttamente'
      : 'Resi chiusi, con alcune righe da controllare';
  }

  /**
   * Riga o reso a cui si riferisce una voce degli elenchi. Il backend non fissa
   * il nome del campo, quindi si tenta con quelli plausibili.
   */
  rowLabel(item: any): string {
    if (item == null || typeof item !== 'object') {
      return '';
    }
    const rowNumber = item.rowNumber ?? item.row ?? item.line ?? item.lineNumber ?? item.riga;
    if (rowNumber != null) {
      return `Riga ${rowNumber}`;
    }
    return item.palletCode ?? item.code ?? '';
  }

  /** Motivo per cui una riga è stata saltata o scartata */
  rowReason(item: any): string {
    if (item == null) {
      return '';
    }
    if (typeof item === 'string') {
      return item;
    }
    return item.reason ?? item.message ?? item.error ?? item.description
      ?? item.motivo ?? JSON.stringify(item);
  }
}
